import type { EntityTypeName } from '@/lib/crm/core/types';
import { useEntityTypeSchema } from '@/lib/crm/hooks/useEntityTypeSchema';

export interface EntityTypeValueLabelProps {
  value: string | null | undefined;
  orgId?: string | number;
  fontSize?: 'xs' | 'table' | 'sm' | 'base' | 'lg';
  className?: string;
  truncate?: boolean;
}

const fontSizeClasses: Record<string, string> = {
  xs: 'text-xs',
  table: 'text-[13px]',
  sm: 'text-sm',
  base: 'text-base',
  lg: 'text-lg',
};

/**
 * Display component for entity_type field values.
 *
 * Resolves an entity type's stable name (e.g. "ahoy_contact") to its
 * human-readable label (e.g. "Contact") via the org's entity-type schema.
 * Falls back to the raw value while loading or if the type can't be resolved.
 */
export const EntityTypeValueLabel: React.FC<EntityTypeValueLabelProps> = ({
  value,
  orgId,
  fontSize = 'sm',
  className = '',
  truncate,
}) => {
  const sizeClass = fontSizeClasses[fontSize];
  const truncateClasses = truncate
    ? 'block overflow-hidden text-ellipsis whitespace-nowrap'
    : '';

  const { schema } = useEntityTypeSchema(
    orgId,
    value ? (value as EntityTypeName) : undefined
  );

  if (value === null || value === undefined || value === '') {
    return <span className={`${sizeClass} ${className}`}>—</span>;
  }

  const displayValue = schema?.label ?? value;

  return (
    <span
      className={`${sizeClass} ${truncateClasses} ${className}`}
      title={truncate ? displayValue : undefined}
    >
      {displayValue}
    </span>
  );
};
